import React, { useState } from "react";
import Button from "@hui/_atoms/button/Button";
import { withdrawFunds } from "@api/cards.api";
import I18n from "../../../utilities/translations";
import Modal from "../../_molecules/modal/Modal";
import ManageFundsOptions from "./ManageFundsOptions";
import TopupFundsViaBank from "../card/TopupFundsViaBank";
import WithdrawFunds from "../card/WithdrawFunds";

interface iManageFundsModal {
  title: string;
  buttonText: string;
  yourAccountNumber: string;
  hnryAccountName: string;
  hnryAccountNumber: string;
  hnryReference: string;
  availableBalance: number;
  canWithdraw: boolean;
  jurisdiction: string;
  hnryAccountBalanceFeature: boolean;
}

const ManageFundsModal = ({
  title,
  buttonText,
  yourAccountNumber,
  hnryAccountName,
  hnryAccountNumber,
  hnryReference,
  availableBalance,
  canWithdraw,
  jurisdiction,
  hnryAccountBalanceFeature,
}: iManageFundsModal) => {
  const MAIN_MODAL = "manage_funds";
  const TOPUP_VIA_BANK_MODAL = "topup_funds";
  const WITHDRAW_FUNDS_MODAL = "withdraw_funds";

  const [showModal, setShowModal] = useState(false);
  const [currentModal, setCurrentModal] = useState(MAIN_MODAL);
  const [modalTitle, setModalTitle] = useState(title);
  const [confirmButton, setConfirmButton] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [disabledSubmit, setDisabledSubmit] = useState(false);

  const handleModalChange = (nextModal) => {
    setCurrentModal(nextModal);
  };

  const reset = () => {
    if (currentModal === WITHDRAW_FUNDS_MODAL) {
      setWithdrawalAmount("");
    }
    setShowModal(false);
  };

  const handleWithdrawSubmit = async () => {
    try {
      setDisabledSubmit(true);
      const response = await withdrawFunds({ amount: withdrawalAmount });

      if (response.status === "bad_request") {
        toastr.error(response.message);
      } else if (response.status === "ok") {
        toastr.success(response.message);
      } else {
        toastr.success(I18n.t("cards.manage_funds.withdraw_funds.error"));
      }
    } catch (error) {
      if (typeof Rollbar !== "undefined") {
        Rollbar.warning("User unable to withdraw funds from card", { error });
      }
    } finally {
      reset();
    }
  };

  const handleOpenModal = () => {
    setModalTitle(title);
    handleModalChange(MAIN_MODAL);
    setShowModal(!showModal);
  };

  const handleConfirm = () => {
    if (currentModal === WITHDRAW_FUNDS_MODAL) {
      handleWithdrawSubmit();
    }
    setShowModal(!showModal);
    setDisabledSubmit(false);
  };

  const handleAddFundsAutomaticallyClick = () => {
    // TO DO
  };

  const handleAddFundsViaBankTransferClick = () => {
    setModalTitle("Add funds via bank transfer");
    handleModalChange(TOPUP_VIA_BANK_MODAL);
    setConfirmButton("Done");
    setDisabledSubmit(false);
  };

  const handleWithdrawFundsClick = () => {
    setModalTitle("Withdraw funds from your Hnry Account");
    handleModalChange(WITHDRAW_FUNDS_MODAL);
    setConfirmButton("Confirm");
    setDisabledSubmit(true);
  };

  const handleBack = () => {
    if (currentModal === WITHDRAW_FUNDS_MODAL) {
      setWithdrawalAmount("");
    }
    setModalTitle(title);
    handleModalChange(MAIN_MODAL);
  };

  return (
    <>
      <Button classes="!tw-px-3" onClick={handleOpenModal} variant="secondary">
        <span>{buttonText}</span>
      </Button>
      {showModal && (
        <Modal
          open={showModal}
          setOpen={setShowModal}
          title={modalTitle}
          closable
          onCancel={reset} >
          { (currentModal === MAIN_MODAL) &&
          <ManageFundsOptions
            onAddFundsAutomaticallyClick={handleAddFundsAutomaticallyClick}
            onAddFundsViaBankTransferClick={handleAddFundsViaBankTransferClick}
            onWithdrawFundsClick={handleWithdrawFundsClick}
          />
          }
          { (currentModal === TOPUP_VIA_BANK_MODAL) &&
          <TopupFundsViaBank
            yourAccountNumber={yourAccountNumber}
            hnryAccountName={hnryAccountName}
            hnryAccountNumber={hnryAccountNumber}
            hnryReference={hnryReference}
          />
          }
          { (currentModal === WITHDRAW_FUNDS_MODAL) &&
          <WithdrawFunds
            availableBalance={availableBalance}
            withdrawalAmount={withdrawalAmount}
            setWithdrawalAmount={setWithdrawalAmount}
            setDisabledSubmit={setDisabledSubmit}
            canWithdraw={canWithdraw}
            jurisdiction={jurisdiction} 
            hnryAccountBalanceFeature={hnryAccountBalanceFeature}            
          />
          }
          { (currentModal !== MAIN_MODAL) &&
            <div className="hnry-dialog-panel-actions">
              <Button classes="!tw-px-3" onClick={handleConfirm} disabled={disabledSubmit} variant="primary">
                <span>{confirmButton}</span>
              </Button>
              <Button classes="!tw-px-3" onClick={handleBack} variant="secondary">
                <span>Back</span>
              </Button>
            </div>
          }
        </Modal>
      )}
    </>
  );
};

export default ManageFundsModal;
